import type { cookies } from 'next/headers'
import xss from 'xss'

type NextCookies = Awaited<ReturnType<typeof cookies>>

// https://giphy.com/search/%08%3E%3Cscript%3Ealert%28origin%29%3C%2Fscript
export const safeDecodeURIComponent = (str: string = '') => {
    try {
        const decoded = decodeURIComponent(str)
        return xss(decoded)
    } catch (e) {
        console.error(`error decoding ${str}`, e)
    }
    return str
}

export const getCookieString = (cookies: NextCookies) => {
    return cookies
        .getAll()
        .map(({ name, value }) => `${name}=${value}`)
        .join('; ')
}

export type Cookies = string | NextCookies

const SCRIPT_ESCAPE_MAP: Record<string, string> = {
    '<': '\\u003c',
    '>': '\\u003e',
    '&': '\\u0026',
    '\u2028': '\\u2028',
    '\u2029': '\\u2029',
}

export const serializeJsonForScript = (value: unknown) =>
    JSON.stringify(value).replace(/[<>&\u2028\u2029]/g, (character) => SCRIPT_ESCAPE_MAP[character])

/**
 * Sanitizes and normalizes a URL to use https.
 */
export const normalizeUrl = (value: string = '') => {
    const sanitized = xss(value).trim()
    if (!sanitized || sanitized.startsWith('/') || sanitized.startsWith('#')) return ''

    const candidate = sanitized.startsWith('//')
        ? `https:${sanitized}`
        : /^[a-z][a-z\d+\-.]*:/i.test(sanitized)
          ? sanitized
          : `https://${sanitized}`

    try {
        const normalized = new URL(candidate)
        if (normalized.protocol !== 'http:' && normalized.protocol !== 'https:') {
            return ''
        }
        normalized.protocol = 'https:'

        const hasExplicitRootSlash =
            /^\/\/[^/?#]+\/(?:[?#].*)?$/i.test(sanitized) ||
            /^[a-z][a-z\d+\-.]*:\/\/[^/?#]+\/(?:[?#].*)?$/i.test(sanitized) ||
            /^[^/?#]+\/(?:[?#].*)?$/i.test(sanitized)

        if (normalized.pathname === '/' && !hasExplicitRootSlash) {
            return `${normalized.origin}${normalized.search}${normalized.hash}`
        }

        return normalized.toString()
    } catch {
        return ''
    }
}
/**
 * util mostly just to set cookies correctly on client and server
 */
export const GET = (cookie: Cookies = ''): RequestInit => {
    if (typeof cookie === 'object') {
        cookie = getCookieString(cookie as NextCookies)
    }
    return {
        method: 'get',
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            cookie: cookie || (typeof document === 'undefined' ? '' : document?.cookie),
        },
    }
}
